import { copyToClipboard, shareContent } from "@lib/clipboard";
import { downloadFile, fetchJson } from "@lib/api";

interface AccessTokenResponse {
  token: string;
  expires_at: string;
  checkin_url: string;
}

interface DoorSaleLinkResponse {
  purchase_url: string;
  qr_url: string;
  expires_at: string;
}

interface DiscountLink {
  id: number;
  price_amount: number;
  expires_at: string;
  max_tickets: number | null;
  tickets_sold: number;
  is_active: boolean;
  purchase_url: string;
}

interface DiscountLinksResponse {
  links: DiscountLink[];
}

let currentCheckinLink: string | null = null;
let currentDoorSaleLink: string | null = null;
let currentDoorQrUrl: string | null = null;
let currentDiscountEventId = "";

function toggleFilter(
  filterType: "upcoming" | "past" | "disabled" | "archived",
): void {
  const pill = document.getElementById(`filter-${filterType}`);
  if (!pill) return;

  const isActive = pill.getAttribute("data-active") === "true";
  const newState = !isActive;

  pill.setAttribute("data-active", String(newState));

  const label = pill.textContent?.replace(/\s*[✓✗]\s*$/, "").trim() || "";

  if (newState) {
    pill.classList.add("filter-pill-active");
    pill.textContent = `${label} ✓`;
  } else {
    pill.classList.remove("filter-pill-active");
    pill.textContent = `${label} ✗`;
  }

  applyFilters();
}

function applyFilters(): void {
  const showUpcoming =
    document.getElementById("filter-upcoming")?.getAttribute("data-active") ===
    "true";
  const showPast =
    document.getElementById("filter-past")?.getAttribute("data-active") ===
    "true";
  const showDisabled =
    document.getElementById("filter-disabled")?.getAttribute("data-active") ===
    "true";
  const showArchived =
    document.getElementById("filter-archived")?.getAttribute("data-active") ===
    "true";

  const cards = document.querySelectorAll<HTMLElement>(".event-card");

  cards.forEach((card) => {
    const isUpcoming = card.getAttribute("data-event-upcoming") === "true";
    const isPast = card.getAttribute("data-event-past") === "true";
    const isDisabled = card.getAttribute("data-event-disabled") === "true";
    const isArchived = card.getAttribute("data-event-archived") === "true";

    const shouldShow = (() => {
      if (isUpcoming && showUpcoming) return true;
      if (isPast && showPast) return true;
      if (isDisabled && showDisabled) return true;
      if (isArchived && showArchived) return true;
      return false;
    })();

    card.style.display = shouldShow ? "block" : "none";
  });
}

async function copyPurchaseLink(eventId: string): Promise<void> {
  const url = `${window.location.origin}/purchase?event_id=${eventId}`;
  await copyToClipboard(url, `Purchase link copied to clipboard!\n\n${url}`);
}

async function shareEvent(eventId: string, eventName: string): Promise<void> {
  const eventUrl = `${window.location.origin}/purchase?event_id=${eventId}`;
  await shareContent({
    title: eventName,
    text: `Check out this event: ${eventName}`,
    url: eventUrl,
  });
}

async function showCheckinLink(
  eventId: string,
  eventName: string,
): Promise<void> {
  const modalEventName = document.getElementById("modal-event-name");
  if (modalEventName) {
    modalEventName.textContent = eventName;
  }

  try {
    const data = await fetchJson<AccessTokenResponse>(
      `/checkin/access-token/${eventId}`,
    );
    const fullUrl = `${window.location.origin}${data.checkin_url}`;

    currentCheckinLink = fullUrl;

    const linkExpiry = document.getElementById("link-expiry");
    if (linkExpiry) {
      linkExpiry.textContent = new Date(data.expires_at).toLocaleString();
    }

    const openLink = document.getElementById(
      "open-checkin-link",
    ) as HTMLAnchorElement | null;
    if (openLink) {
      openLink.href = `/checkin/${eventId}`;
    }

    const modal = document.getElementById("checkin-modal");
    if (modal) {
      modal.style.display = "flex";
    }
  } catch (error) {
    console.error("Failed to generate check-in link:", error);
    alert("Failed to generate check-in link. Please try again.");
  }
}

function closeCheckinModal(): void {
  const modal = document.getElementById("checkin-modal");
  if (modal) {
    modal.style.display = "none";
  }
}

async function copyCheckinLink(): Promise<void> {
  if (!currentCheckinLink) return;
  await copyToClipboard(
    currentCheckinLink,
    `Check-in link copied to clipboard!\n\n${currentCheckinLink}`,
  );
}

async function shareCheckinLink(): Promise<void> {
  if (!currentCheckinLink) return;

  const eventName =
    document.getElementById("modal-event-name")?.textContent ?? "Event";
  await shareContent({
    title: `Check-In: ${eventName}`,
    text: `Check-in link for ${eventName}`,
    url: currentCheckinLink,
  });
}

async function downloadWithButtonFeedback(
  btnId: string,
  url: string,
  defaultFilename: string,
  errorPrefix: string,
): Promise<void> {
  const btn = document.getElementById(btnId);
  if (!btn) return;

  const originalText = btn.textContent ?? "";
  btn.textContent = "⏳ Generating...";
  (btn as HTMLButtonElement).style.pointerEvents = "none";
  (btn as HTMLButtonElement).style.opacity = "0.6";

  try {
    await downloadFile(url, defaultFilename);

    btn.textContent = "✓ Downloaded";
    (btn as HTMLButtonElement).style.backgroundColor = "#a3be8c";
    setTimeout(() => {
      btn.textContent = originalText;
      (btn as HTMLButtonElement).style.backgroundColor = "";
      (btn as HTMLButtonElement).style.pointerEvents = "";
      (btn as HTMLButtonElement).style.opacity = "";
    }, 2000);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    console.error(`${errorPrefix}:`, error);
    alert(`${errorPrefix}: ${message}`);
    btn.textContent = originalText;
    (btn as HTMLButtonElement).style.pointerEvents = "";
    (btn as HTMLButtonElement).style.opacity = "";
  }
}

async function downloadCsv(eventId: string): Promise<void> {
  await downloadWithButtonFeedback(
    `csv-btn-${eventId}`,
    `/api/events/${encodeURIComponent(eventId)}/attendees/csv`,
    `attendees_${eventId}.csv`,
    "Failed to download CSV",
  );
}

async function downloadPayoutReport(eventId: string): Promise<void> {
  await downloadWithButtonFeedback(
    `payout-btn-${eventId}`,
    `/payout-report?event_id=${encodeURIComponent(eventId)}`,
    `payout_report_${eventId}.csv`,
    "Failed to download payout report",
  );
}

async function showDoorSaleLink(
  eventId: string,
  eventName: string,
): Promise<void> {
  const modalEventName = document.getElementById("door-modal-event-name");
  if (modalEventName) {
    modalEventName.textContent = eventName;
  }

  try {
    const data = await fetchJson<DoorSaleLinkResponse>(
      `/admin/events/${eventId}/door-sale-link`,
    );
    currentDoorSaleLink = data.purchase_url;
    currentDoorQrUrl = data.qr_url;

    const linkExpiry = document.getElementById("door-link-expiry");
    if (linkExpiry) {
      linkExpiry.textContent = new Date(data.expires_at).toLocaleString();
    }

    const modal = document.getElementById("door-sale-modal");
    if (modal) {
      modal.style.display = "flex";
    }
  } catch (error) {
    console.error("Failed to generate door sale link:", error);
    alert("Failed to generate door sale link. Please try again.");
  }
}

function closeDoorSaleModal(): void {
  const modal = document.getElementById("door-sale-modal");
  if (modal) {
    modal.style.display = "none";
  }
}

async function copyDoorSaleLink(): Promise<void> {
  if (!currentDoorSaleLink) return;
  await copyToClipboard(
    currentDoorSaleLink,
    `Door sale link copied to clipboard!\n\n${currentDoorSaleLink}`,
  );
}

async function shareDoorSaleLink(): Promise<void> {
  if (!currentDoorSaleLink) return;

  const eventName =
    document.getElementById("door-modal-event-name")?.textContent ?? "Event";
  await shareContent({
    title: `Door Sales: ${eventName}`,
    text: `Purchase tickets at the door for ${eventName}`,
    url: currentDoorSaleLink,
  });
}

function viewDoorSaleQr(): void {
  if (!currentDoorQrUrl) return;
  window.open(currentDoorQrUrl, "_blank");
}

async function showDiscountLinksModal(
  eventId: string,
  eventName: string,
): Promise<void> {
  currentDiscountEventId = eventId;
  const modalEventName = document.getElementById("discount-modal-event-name");
  if (modalEventName) {
    modalEventName.textContent = eventName;
  }

  const modal = document.getElementById("discount-links-modal");
  if (modal) {
    modal.style.display = "flex";
  }

  await loadDiscountLinks(eventId);
}

function closeDiscountLinksModal(): void {
  const modal = document.getElementById("discount-links-modal");
  if (modal) {
    modal.style.display = "none";
  }
  currentDiscountEventId = "";
}

async function loadDiscountLinks(eventId: string): Promise<void> {
  const container = document.getElementById("discount-links-container");
  if (!container) return;

  container.innerHTML = '<p style="color: #88c0d0;">Loading...</p>';

  try {
    const data = await fetchJson<DiscountLinksResponse>(
      `/api/events/${eventId}/discount-links`,
    );

    if (data.links.length === 0) {
      container.innerHTML =
        '<p style="color: #88c0d0;">No discount links created yet.</p>';
      return;
    }

    function getLinkStatus(link: DiscountLink) {
      if (!link.is_active) {
        return { text: "Deactivated", color: "#bf616a" };
      }

      const now = new Date();
      const expiresAt = new Date(link.expires_at);

      if (expiresAt < now) {
        return { text: "Expired", color: "#d08770" };
      }

      if (link.max_tickets && link.tickets_sold >= link.max_tickets) {
        return { text: "Max Tickets Reached", color: "#d08770" };
      }

      return { text: "Active", color: "#a3be8c" };
    }

    container.innerHTML = data.links
      .map((link) => {
        const status = getLinkStatus(link);
        const isUsable = (() => {
          if (!link.is_active) return false;
          if (new Date(link.expires_at) < new Date()) return false;
          if (link.max_tickets && link.tickets_sold >= link.max_tickets)
            return false;
          return true;
        })();

        return `
                <div style="background: #3b4252; padding: 12px; border-radius: 4px; margin-bottom: 10px; ${!isUsable ? "opacity: 0.6;" : ""}" data-testid="discount-link-item">
                    <div style="display: flex; justify-content: space-between; align-items: start;">
                        <div style="flex: 1;">
                            <p style="margin: 2px 0;"><strong>Price:</strong> $${(link.price_amount / 100).toFixed(2)}</p>
                            <p style="margin: 2px 0; font-size: 0.85em;"><strong>Tickets:</strong> ${link.tickets_sold}${link.max_tickets ? ` / ${link.max_tickets}` : " (unlimited)"}</p>
                            <p style="margin: 2px 0; font-size: 0.85em;"><strong>Expires:</strong> ${new Date(link.expires_at).toLocaleString()}</p>
                            <p style="margin: 2px 0; font-size: 0.85em;"><strong>Status:</strong> <span style="color: ${status.color};">${status.text}</span></p>
                            <p style="margin: 2px 0; font-size: 0.85em;"><a href="${link.purchase_url}" target="_blank" style="color: #88c0d0; text-decoration: none;" data-testid="discount-link-url">🔗 Link</a></p>
                        </div>
                        <div style="display: flex; gap: 5px; flex-direction: column;">
                            <button onclick="copyDiscountLink('${link.purchase_url}')" style="padding: 6px 10px; background: #88c0d0; color: #2e3440; border: none; border-radius: 4px; cursor: pointer; font-size: 0.85em;">📋 Copy</button>
                            ${link.is_active ? `<button onclick="deactivateDiscountLink(${link.id})" style="padding: 6px 10px; background: #bf616a; color: #eceff4; border: none; border-radius: 4px; cursor: pointer; font-size: 0.85em;">Deactivate</button>` : `<button onclick="reactivateDiscountLink(${link.id})" style="padding: 6px 10px; background: #a3be8c; color: #2e3440; border: none; border-radius: 4px; cursor: pointer; font-size: 0.85em;">Reactivate</button>`}
                        </div>
                    </div>
                </div>
            `;
      })
      .join("");
  } catch (error) {
    console.error("Failed to load discount links:", error);
    container.innerHTML =
      '<p style="color: #bf616a;">Failed to load discount links.</p>';
  }
}

async function createDiscountLink(event: Event): Promise<void> {
  event.preventDefault();

  const priceInput = document.getElementById(
    "discount-price",
  ) as HTMLInputElement;
  const expiresInput = document.getElementById(
    "discount-expires-hours",
  ) as HTMLInputElement;
  const maxTicketsInput = document.getElementById(
    "discount-max-tickets",
  ) as HTMLInputElement;

  const price = parseFloat(priceInput.value);
  const expiresHours = parseInt(expiresInput.value);
  const maxTickets = maxTicketsInput.value
    ? parseInt(maxTicketsInput.value)
    : null;

  try {
    const response = await fetch(
      `/api/events/${currentDiscountEventId}/discount-links`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          price_amount: price,
          expires_hours: expiresHours,
          max_tickets: maxTickets,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to create discount link");
    }

    const form = document.getElementById(
      "create-discount-link-form",
    ) as HTMLFormElement;
    form.reset();
    expiresInput.value = "24";

    await loadDiscountLinks(currentDiscountEventId);
  } catch (error) {
    console.error("Failed to create discount link:", error);
    alert("Failed to create discount link. Please try again.");
  }
}

async function deactivateDiscountLink(linkId: number): Promise<void> {
  if (!confirm("Are you sure you want to deactivate this discount link?")) {
    return;
  }

  try {
    const response = await fetch(
      `/api/events/${currentDiscountEventId}/discount-links/${linkId}`,
      {
        method: "DELETE",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to deactivate discount link");
    }

    await loadDiscountLinks(currentDiscountEventId);
  } catch (error) {
    console.error("Failed to deactivate discount link:", error);
    alert("Failed to deactivate discount link. Please try again.");
  }
}

async function reactivateDiscountLink(linkId: number): Promise<void> {
  if (!confirm("Reactivate this discount link?")) return;

  try {
    const response = await fetch(
      `/api/events/${currentDiscountEventId}/discount-links/${linkId}/reactivate`,
      {
        method: "PATCH",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to reactivate discount link");
    }

    await loadDiscountLinks(currentDiscountEventId);
  } catch (error) {
    console.error("Failed to reactivate discount link:", error);
    alert("Failed to reactivate discount link. Please try again.");
  }
}

async function copyDiscountLink(url: string): Promise<void> {
  await copyToClipboard(url, "Discount link copied to clipboard!");
}

function showArchiveModal(eventId: string, eventName: string): void {
  const modalEventName = document.getElementById("archive-modal-event-name");
  if (modalEventName) {
    modalEventName.textContent = eventName;
  }

  const eventIdInput = document.getElementById(
    "archive-event-id",
  ) as HTMLInputElement;
  if (eventIdInput) {
    eventIdInput.value = eventId;
  }

  const form = document.getElementById("archive-form") as HTMLFormElement;
  if (form) {
    form.action = `/events/${eventId}/archive`;
  }

  const modal = document.getElementById("archive-modal");
  if (modal) {
    modal.style.display = "flex";
  }
}

function closeArchiveModal(): void {
  const modal = document.getElementById("archive-modal");
  if (modal) {
    modal.style.display = "none";
  }
}

// Expose functions to window immediately for onclick handlers in templates
(window as any).toggleFilter = toggleFilter;
(window as any).copyPurchaseLink = copyPurchaseLink;
(window as any).shareEvent = shareEvent;
(window as any).showCheckinLink = showCheckinLink;
(window as any).closeCheckinModal = closeCheckinModal;
(window as any).copyCheckinLink = copyCheckinLink;
(window as any).shareCheckinLink = shareCheckinLink;
(window as any).downloadCsv = downloadCsv;
(window as any).downloadPayoutReport = downloadPayoutReport;
(window as any).showDoorSaleLink = showDoorSaleLink;
(window as any).closeDoorSaleModal = closeDoorSaleModal;
(window as any).copyDoorSaleLink = copyDoorSaleLink;
(window as any).shareDoorSaleLink = shareDoorSaleLink;
(window as any).viewDoorSaleQr = viewDoorSaleQr;
(window as any).showDiscountLinksModal = showDiscountLinksModal;
(window as any).closeDiscountLinksModal = closeDiscountLinksModal;
(window as any).createDiscountLink = createDiscountLink;
(window as any).deactivateDiscountLink = deactivateDiscountLink;
(window as any).reactivateDiscountLink = reactivateDiscountLink;
(window as any).copyDiscountLink = copyDiscountLink;
(window as any).showArchiveModal = showArchiveModal;
(window as any).closeArchiveModal = closeArchiveModal;
